import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import api from "../api/client";

const ROLES = [
  { value: "employee", label: "Employee", desc: "Quiz access only — SSO, guidelines, timed quiz, results", color: "#6b7280" },
  { value: "admin", label: "Admin / HRBP", desc: "View results, track progress, allow retakes", color: "#8b5cf6" },
  { value: "manager", label: "Manager", desc: "Dashboard access — leadership & team views", color: "#3b82f6" },
  { value: "c_level", label: "C-Level", desc: "Full dashboard — overview, leadership, organization", color: "#f59e0b" },
];

export default function TestRoles() {
  const [mode, setMode] = useState("login"); // "login" or "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("employee");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const endpoint = mode === "register" ? "/api/auth/test-register" : "/api/auth/login";
      const payload = mode === "register"
        ? { email, password, name, role }
        : { email, password };
      const res = await api.post(endpoint, payload);
      login(res.data.access_token, res.data.user);
      navigate("/welcome");
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%)",
      padding: "20px",
    }}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{
          background: "rgba(255,255,255,0.05)",
          borderRadius: "16px",
          border: "1px solid rgba(255,255,255,0.1)",
          padding: "40px",
          width: "100%",
          maxWidth: "460px",
          backdropFilter: "blur(20px)",
        }}
      >
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "28px" }}>
          <div style={{
            display: "inline-flex",
            padding: "6px 14px",
            borderRadius: "20px",
            background: "rgba(239,68,68,0.15)",
            border: "1px solid rgba(239,68,68,0.3)",
            marginBottom: "16px",
          }}>
            <span style={{ fontSize: "12px", fontWeight: "600", color: "#ef4444", letterSpacing: "0.5px" }}>
              TEST MODE
            </span>
          </div>
          <h1 style={{ fontSize: "22px", fontWeight: "700", color: "#fff", margin: "0 0 6px" }}>
            RBAC Testing
          </h1>
          <p style={{ fontSize: "13px", color: "rgba(255,255,255,0.5)", margin: 0 }}>
            Create or log into test accounts with specific roles
          </p>
        </div>

        {/* Mode Toggle */}
        <div style={{
          display: "flex",
          gap: "0",
          marginBottom: "24px",
          background: "rgba(255,255,255,0.05)",
          borderRadius: "10px",
          padding: "3px",
        }}>
          {["register", "login"].map((m) => (
            <button
              key={m}
              onClick={() => { setMode(m); setError(""); }}
              style={{
                flex: 1,
                padding: "10px",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "600",
                background: mode === m ? "rgba(255,255,255,0.12)" : "transparent",
                color: mode === m ? "#fff" : "rgba(255,255,255,0.4)",
                transition: "all 0.2s",
              }}
            >
              {m === "register" ? "Sign Up" : "Sign In"}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Role selector — only for register */}
          {mode === "register" && (
            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "rgba(255,255,255,0.6)", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Select Role
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                {ROLES.map((r) => (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => setRole(r.value)}
                    style={{
                      padding: "12px 10px",
                      borderRadius: "10px",
                      border: role === r.value ? `2px solid ${r.color}` : "2px solid rgba(255,255,255,0.08)",
                      background: role === r.value ? `${r.color}15` : "rgba(255,255,255,0.03)",
                      cursor: "pointer",
                      textAlign: "left",
                      transition: "all 0.2s",
                    }}
                  >
                    <div style={{ fontSize: "13px", fontWeight: "600", color: role === r.value ? r.color : "rgba(255,255,255,0.7)", marginBottom: "2px" }}>
                      {r.label}
                    </div>
                    <div style={{ fontSize: "10px", color: "rgba(255,255,255,0.4)", lineHeight: "1.3" }}>
                      {r.desc}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Name — only for register */}
          {mode === "register" && (
            <div style={{ marginBottom: "14px" }}>
              <input
                type="text"
                placeholder="Full Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                style={inputStyle}
              />
            </div>
          )}

          {/* Email */}
          <div style={{ marginBottom: "14px" }}>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={inputStyle}
            />
          </div>

          {/* Password */}
          <div style={{ marginBottom: "20px" }}>
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={4}
              style={inputStyle}
            />
          </div>

          {error && (
            <div style={{
              padding: "10px 14px",
              borderRadius: "8px",
              background: "rgba(239,68,68,0.1)",
              border: "1px solid rgba(239,68,68,0.3)",
              color: "#ef4444",
              fontSize: "13px",
              marginBottom: "16px",
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              padding: "12px",
              borderRadius: "10px",
              border: "none",
              background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
              color: "#fff",
              fontSize: "14px",
              fontWeight: "600",
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.7 : 1,
              transition: "opacity 0.2s",
            }}
          >
            {loading ? "Please wait..." : mode === "register" ? "Create Test Account" : "Sign In"}
          </button>
        </form>

        {/* Back to normal login */}
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          <button
            onClick={() => navigate("/login")}
            style={{
              background: "none",
              border: "none",
              color: "rgba(255,255,255,0.4)",
              fontSize: "12px",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            Back to normal login (SSO)
          </button>
        </div>
      </motion.div>
    </div>
  );
}

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: "10px",
  border: "1px solid rgba(255,255,255,0.1)",
  background: "rgba(255,255,255,0.05)",
  color: "#fff",
  fontSize: "14px",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.2s",
};
